import { AlertService } from '../../../shared/alert/alert.service';
import { CaxtonCardService } from './../../../core/services/caxton-card.service';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { enumToText } from '../../../core/helpers/enum-to-text.function';
import {
  AddCaxtonCardRequest,
  CaxtonCardDelivery,
  CaxtonCardOffice,
  CaxtonCardStatus,
  CaxtonCardTypeFilter,
  CaxtonCardViewModel,
  ExportCaxtonCardsRequest,
  GetCaxtonCardsRequest,
  UpdateCaxtonCardRequest
} from '../../../core/models/card-tracker.model';
import { ResponsePage } from '../../../core/models/search-page.model';
import { SelectOption } from '../../../core/models/select-option.model';
import { DropdownInputComponent } from '../../../shared/dropdown-input/dropdown-input.component';
import { ModalV2Component } from '../../../shared/modal-v2/modal-v2.component';
import { TrialService } from '../../../core/services/trial.service';
import { LogHelper } from '../../../core/helpers/log.helper';
import { TrialAutocompleteComponent } from '../../../shared/trial-autocomplete/trial-autocomplete.component';
import { TrialAutocomplete } from '../../../shared/trial-autocomplete/trial-autocomplete.model';
import { TrialWithSites } from '../../../core/models/trial-detail.model';
import { ModalComponent } from '../../../shared/modal/modal.component';
import moment, { Moment } from 'moment';
import { Permissions } from "../../../core/constants/permissions";
import { AuthService } from "../../../core/services/auth.service";
import { SiteAutocomplete } from "../../../shared/site-autocomplete/site-autocomplete.model";
import {
  CaxtonCardActionSelectorComponent
} from "../caxton-card-action-selector/caxton-card-action-selector.component";
import {
  CaxtonCardBulkUpdateModalComponent
} from "../caxton-card-bulk-update-modal/caxton-card-bulk-update-modal.component";
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { SHORT_DATE_FORMAT } from '../../../core/constants/global-constants';

@Component({
  selector: 'app-caxton-cards-list',
  templateUrl: './caxton-cards-list.component.html',
  styleUrl: './caxton-cards-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    provideMomentDateAdapter(SHORT_DATE_FORMAT),
  ]
})
export class CaxtonCardsListComponent implements OnInit, AfterViewInit {
  @ViewChild('markAsSelect') markAsSelect: DropdownInputComponent;
  @ViewChild('addCardsOfficeDropdown') addCardsOfficeDropdown: DropdownInputComponent;
  @ViewChild('addCardsModal') addCardsModal: ModalV2Component;
  @ViewChild('trialAutocomplete') trialAutocomplete: TrialAutocompleteComponent;
  @ViewChild('exportModal') exportModal: ModalComponent;
  @ViewChild('exportTrialAutocomplete') exportTrialAutocomplete: TrialAutocompleteComponent;
  @ViewChild('actionSelector') actionSelector: CaxtonCardActionSelectorComponent;
  @ViewChild('bulkUpdateModal') bulkUpdateModal: CaxtonCardBulkUpdateModalComponent;
  @ViewChild('confirmCardStateChangeModal') confirmCardStateChangeModal: ModalComponent;

  cardTypeOptions: SelectOption[] = [];
  trialOptions: SelectOption[] = [];
  siteOptions: Record<string, SelectOption[]> = {};
  cardStatusOptions: SelectOption[] = [];
  deliveryOptions: SelectOption[] = [];
  officeOptions: SelectOption[] = [];
  addOfficeOptions: SelectOption[] = [];
  trialsList: TrialWithSites[] = [];

  cards: ResponsePage<CaxtonCardViewModel>;

  expiryDateFilterForm = new FormGroup({
    fromExpiryDate: new FormControl(),
    toExpiryDate: new FormControl(),
  })

  cardTypeFilterForm: FormGroup = new FormGroup({
    cardTypes: new FormControl<string[]>([]),
    cardStatus: new FormControl<string[]>([]),
    office: new FormControl<string[]>([])
  });

  addCardsForm = new FormGroup({
    numberOfCards: new FormControl<number>(null, [Validators.required, Validators.min(1)]),
    lastCardNo: new FormControl<number>(null, Validators.required),
    office: new FormControl<string>(CaxtonCardOffice.UK.toString()),
  });

  searchForm = new FormGroup({
    trialId: new FormControl(''),
    searchTerm: new FormControl(''),
  });

  exportForm = new FormGroup({
    startDate: new FormControl(),
    endDate: new FormControl(),
    trialId: new FormControl('', [Validators.required]),
    allTrials: new FormControl(false),
  });

  selectAllForm: FormGroup = new FormGroup({
    selectAll: new FormControl(false)
  });

  updateCardForms: Record<string, FormGroup> = {};

  processingModalRequest: boolean = false;
  loading: boolean = false;

  protected canWrite: boolean = false;
  protected canExport: boolean = false;

  CaxtonCardTypeFilter = CaxtonCardTypeFilter;
  CaxtonCardOffice = CaxtonCardOffice;
  page: number;

  constructor(
    private caxtonCardService: CaxtonCardService,
    private alertService: AlertService,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    private trialService: TrialService) { }

  ngOnInit(): void {
    this.canWrite = this.authService.hasPermission(Permissions.CaxtonCardWrite);
    this.canExport = this.authService.hasPermission(Permissions.CaxtonCardExport);

    this.initDeliveryOptions();
    this.initCardTypeOptions();
    this.initCardStatusOptions();
    this.initOfficeOptions();
    this.initTrialOptions();
  }

  ngAfterViewInit(): void {
    const mainContainer = document.querySelector('.main-container');
    mainContainer.classList.add('full-width');

    this.exportForm.get('allTrials').valueChanges.subscribe((allTrials) => {
      if (allTrials) {
        this.exportForm.get('trialId').setValidators(null);
        this.exportForm.patchValue({ trialId: null });
      } else {
        this.exportForm.get('trialId').setValidators(Validators.required);
      }

      this.exportForm.get('trialId').updateValueAndValidity();
    });

    this.selectAllForm.get('selectAll').valueChanges.subscribe((selectAll) => {
      this.cards.results.forEach(card => {
        if (selectAll) {
          this.actionSelector.addCard(card.id);
        } else {
          this.actionSelector.removeCard(card.id);

        }
      });
    });

    this.cardTypeFilterForm.get('cardTypes').valueChanges.subscribe((cardTypes) => {
      this.loadCards(1);
    });

    this.cardTypeFilterForm.get('cardStatus').valueChanges.subscribe((cardStatus) => {
      this.loadCards(1);
    });

    this.cardTypeFilterForm.get('office').valueChanges.subscribe((office) => {
      this.loadCards(1);
    });

    this.cd.detectChanges();
  }

  onSelectAll() {

  }

  onDeselectAll() {

  }

  initCardTypeOptions() {
    //this.cardTypeOptions.push({ value: CaxtonCardTypeFilter.AllCards.toString(), text: enumToText(CaxtonCardTypeFilter, CaxtonCardTypeFilter.AllCards) });
    this.cardTypeOptions.push({ value: CaxtonCardTypeFilter.Missing.toString(), text: enumToText(CaxtonCardTypeFilter, CaxtonCardTypeFilter.Missing) });
    this.cardTypeOptions.push({ value: CaxtonCardTypeFilter.Ordered.toString(), text: enumToText(CaxtonCardTypeFilter, CaxtonCardTypeFilter.Ordered) });
    this.cardTypeOptions.push({ value: CaxtonCardTypeFilter.Received.toString(), text: enumToText(CaxtonCardTypeFilter, CaxtonCardTypeFilter.Received) });
  }

  initDeliveryOptions() {
    this.deliveryOptions.push({ value: CaxtonCardDelivery.Ordered.toString(), text: enumToText(CaxtonCardDelivery, CaxtonCardDelivery.Ordered) });
    this.deliveryOptions.push({ value: CaxtonCardDelivery.Received.toString(), text: enumToText(CaxtonCardDelivery, CaxtonCardDelivery.Received) });
    this.deliveryOptions.push({ value: CaxtonCardDelivery.Sent.toString(), text: enumToText(CaxtonCardDelivery, CaxtonCardDelivery.Sent) });
    this.deliveryOptions.push({ value: CaxtonCardDelivery.Missing.toString(), text: enumToText(CaxtonCardDelivery, CaxtonCardDelivery.Missing) });
  }

  initCardStatusOptions() {
    this.cardStatusOptions.push({ value: CaxtonCardStatus.Activated.toString(), text: enumToText(CaxtonCardStatus, CaxtonCardStatus.Activated) });
    this.cardStatusOptions.push({ value: CaxtonCardStatus.Blocked.toString(), text: enumToText(CaxtonCardStatus, CaxtonCardStatus.Blocked) });
    this.cardStatusOptions.push({ value: CaxtonCardStatus.Lost.toString(), text: enumToText(CaxtonCardStatus, CaxtonCardStatus.Lost) });
    this.cardStatusOptions.push({ value: CaxtonCardStatus.Stolen.toString(), text: enumToText(CaxtonCardStatus, CaxtonCardStatus.Stolen) });
    this.cardStatusOptions.push({ value: CaxtonCardStatus.Archived.toString(), text: enumToText(CaxtonCardStatus, CaxtonCardStatus.Archived) });
  }

  initOfficeOptions() {
    this.officeOptions.push({ value: CaxtonCardOffice.UK.toString(), text: 'UK' });
    this.officeOptions.push({ value: CaxtonCardOffice.US.toString(), text: 'US' });

    this.addOfficeOptions.push({ value: CaxtonCardOffice.UK.toString(), text: 'UK' });
    this.addOfficeOptions.push({ value: CaxtonCardOffice.US.toString(), text: 'US' });
  }

  initTrialOptions() {
    this.trialService.getTrialsWithSites().subscribe({
      next: response => {
        this.trialOptions = response.map(r => new SelectOption(r.id, r.label));
        this.trialsList = response;

        this.loadCards();
      },
      error: error => {
        this.alertService.showWarningAlert("Failed to get trials for trial dropdown. Please try again or contact support");
        LogHelper.log(error);
      }
    })
  }

  loadCards(page: number = 1) {
    this.page = page;
    this.loading = true;

    let filterValue = this.cardTypeFilterForm.get('cardTypes').value;
    let filter = filterValue.length > 0 ? filterValue.join(',') : CaxtonCardTypeFilter.AllCards.toString();

    let cardStatusValue = this.cardTypeFilterForm.get('cardStatus').value;
    let cardStatus = cardStatusValue.length > 0 ? cardStatusValue.join(',') : '';

    let officeValue = this.cardTypeFilterForm.get('office').value;
    let office = officeValue.length > 0 ? officeValue.join(',') : '';

    const request: GetCaxtonCardsRequest = {
      page: page,
      filter: filter,
      cardStatus: cardStatus,
      office: office,
      trialId: this.searchForm.controls.trialId.value,
      searchTerm: this.searchForm.controls.searchTerm.value,
      expiryFrom: this.expiryDateFilterForm.controls.fromExpiryDate.value,
      expiryTo: this.expiryDateFilterForm.controls.toExpiryDate.value,
    }

    this.caxtonCardService.getCaxtonCards(request).subscribe({
      next: response => {
        this.cards = response;
        this.resetSelectAllCheckbox();
        this.actionSelector.clearSelection();

        this.cards.results.forEach(r => {
          var x = r.patientId;
          if (r.trialId !== null) {
            let trialSites = this.trialsList.find(t => t.id === r.trialId);
            this.siteOptions[r.id] = trialSites?.sites.map(r => new SelectOption(r.id, `${r.name}-${r.address}`));
          }

          // Convert date sent to date object
          if (r.dateSent)
            r.dateSent = new Date(r.dateSent);

          this.updateCardForms[r.id] = new FormGroup({
            id: new FormControl(r.id),
            name: new FormControl(r.name, Validators.required),
            delivery: new FormControl(r.delivery?.toString()),
            expiry: new FormControl(r.expiry),
            office: new FormControl(r.office?.toString()),
            trialId: new FormControl(r.trialId),
            siteId: new FormControl(r.siteId),
            siteName: new FormControl(r.siteName),
            siteNumber: new FormControl(r.siteNumber),
            address: new FormControl(r.address),
            siteContact: new FormControl(r.siteContact),
            dateSent: new FormControl(r.dateSent),
            courierCost: new FormControl(r.courierCost),
            trackingNumber: new FormControl(r.trackingNumber),
            status: new FormControl(r.status?.toString()),
            cardInfo: new FormControl(r.cardInfo),
          });

          this.updateCardForms[r.id].get('trialId').valueChanges.subscribe(trialId => {
            if (!trialId) {
              this.updateCardForms[r.id].patchValue({
                siteId: null,
                siteName: null,
                siteNumber: null
              });
            }
          })
        });

        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.alertService.showErrorResponse(error);
      }
    })
  }

  showAddCardsModal() {
    this.addCardsForm.reset({ office: CaxtonCardOffice.UK.toString() });
    this.addCardsOfficeDropdown?.setValue(CaxtonCardOffice.UK.toString());
    this.caxtonCardService.getLastCardNumber().subscribe({
      next: rsp => {
        this.addCardsForm.patchValue({ lastCardNo: rsp.no + 1, office: this.addCardsForm.get('office').value ?? CaxtonCardOffice.UK.toString() });
        this.addCardsOfficeDropdown?.setValue(this.addCardsForm.get('office').value ?? CaxtonCardOffice.UK.toString());
      },
      error: error => {
        this.loading = false;
        this.alertService.showErrorResponse(error);
      }
    });
    this.addCardsModal.show();
  }

  clearAddCardsOffice() {
    this.addCardsForm.patchValue({ office: null });
    this.addCardsOfficeDropdown?.reset();
  }

  onAddCards() {
    this.processingModalRequest = true;
    const request: AddCaxtonCardRequest = {
      numberOfCards: this.addCardsForm.get('numberOfCards').value,
      lastCardNo: this.addCardsForm.get('lastCardNo').value,
      office: this.toOffice(this.addCardsForm.get('office').value)
    };

    this.caxtonCardService.addCards(request).subscribe({
      next: () => {
        this.loadCards(this.page);
        this.processingModalRequest = false;
        this.alertService.showSuccessAlert("Cards successfully added.");
        this.addCardsModal.hide();
      },
      error: error => {
        this.processingModalRequest = false;
        this.alertService.showErrorResponse(error.error);
      }
    });
  }

  onCardSelected(selected: boolean, cardId: string) {
    if (selected) {
      this.actionSelector.addCard(cardId);
    } else {
      this.actionSelector.removeCard(cardId);
    }
  }

  onCardStateChange(cardId: string) {
    let card = this.cards.results.find(c => c.id === cardId);
    if (!card.patientId) {
      // this card is not assign to a patient, update as normal
      this.onChangesMade(cardId);
      return;
    }

    // this card is linked to a patient - request user confirmation before changing state
    this.confirmCardStateChangeModal.showUsingDeleteId(cardId); // excuse the badly named method - re-using it from another location
  }

  onCardStateChangeCancel(cardId: string) {
    // revert state
    let card = this.cards.results.find(c => c.id === cardId);
    this.updateCardForms[cardId].patchValue({
      status: card.status.toString()
    });

    this.confirmCardStateChangeModal.hide();
    this.confirmCardStateChangeModal.resetDeleteIndexAndId();
  }

  onCardStateChangeConfirm(cardId: string) {
    this.processingModalRequest = true;

    // save changes
    this.onChangesMade(cardId, () => {
      this.processingModalRequest = false;
      this.confirmCardStateChangeModal.hide();
      this.confirmCardStateChangeModal.resetDeleteIndexAndId();
    });
  }

  onChangesMade(cardId: string, finallyCallback: Function = null) {
    const updateCardRequest: UpdateCaxtonCardRequest = this.updateCardForms[cardId].value;
    updateCardRequest.dateSent = this.updateCardForms[cardId].get('dateSent').value !== null ? moment(this.updateCardForms[cardId].get('dateSent').value).format('DD/MMM/yyyy') : null;
    updateCardRequest.expiry = this.updateCardForms[cardId].get('expiry').value;
    updateCardRequest.office = this.toOffice(this.updateCardForms[cardId].get('office').value);

    this.caxtonCardService.updateCard(updateCardRequest).subscribe({
      next: (result) => {
        if (updateCardRequest.delivery == CaxtonCardDelivery.Sent && updateCardRequest.dateSent === null) {
          this.loadCards(this.page);
        }

        if (result.message) {
          if (result.updateSuccessful) {
            this.alertService.showSuccessAlert(result.message);
          } else {
            this.alertService.showWarningAlert(result.message);
          }
        } else {
          this.alertService.showSuccessAlert("Card successfully updated.");
        }

        finallyCallback && finallyCallback();
      },
      error: error => {
        this.alertService.showErrorResponse(error.error);
        finallyCallback && finallyCallback();
      }
    })
  }

  onSiteChanged(cardId: string, siteAutocomplete: SiteAutocomplete) {
    let card = this.cards.results.find(c => c.id === cardId);
    if (siteAutocomplete === null) {
      card.address = null;
      this.updateCardForms[cardId].get("address").setValue(null);
      this.onChangesMade(cardId);
      return;
    }

    card.address = siteAutocomplete.addressAsString();
    this.updateCardForms[cardId].get("address").setValue(siteAutocomplete.addressAsString());

    this.onChangesMade(cardId);
  }

  onTrialChanged(cardId: string, event: any) {
    let card = this.cards.results.find(c => c.id === cardId);

    if (event.value !== null) {
      let trial = this.trialsList.find(t => t.id === event.value);
      card.oracleCode = trial.oracleCode;
    }

    card.siteId = null;
    card.siteNumber = null;
    card.siteName = null;
    card.address = null;
    this.updateCardForms[cardId].get('siteId').setValue(null);
    this.updateCardForms[cardId].get('siteName').setValue(null);
    this.updateCardForms[cardId].get('siteNumber').setValue(null);
    this.updateCardForms[cardId].get('address').setValue(null);

    this.onChangesMade(cardId);
  }

  onCardsAssigned() {
    this.resetSelectAllCheckbox();
    this.actionSelector.clearSelection();
    this.loadCards(this.page);
  }

  onCardsDeselected()
  {
    this.resetSelectAllCheckbox();
  }

  private resetSelectAllCheckbox()
  {
    this.selectAllForm.get('selectAll').setValue(false, { emitEvent: false });
  }

  showUpdateCardsModal() {
    this.bulkUpdateModal.show();
  }

  onExportTrialChanged(selection: TrialAutocomplete): void {
    if (selection) {
      this.exportForm.controls.trialId.setValue(selection.id);
    }
  }

  export() {
    this.processingModalRequest = true;
    const startDate = this.exportForm.get('startDate').value as Date;
    const endDate = this.exportForm.get('endDate').value as Date;

    let request: ExportCaxtonCardsRequest = {
      trialId: this.exportForm.get('trialId').value,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null
    }

    this.caxtonCardService.exportCards(request).subscribe({
      next: () => {
        this.alertService.showSuccessAlert("The export request was sent, please check your email.");
        this.exportModal.hide();
        this.processingModalRequest = false;
      },
      error: err => {
        this.alertService.showErrorResponse(err.error)
        this.processingModalRequest = false;
      }
    });
  }

  showExportModal() {
    this.exportForm.reset();
    this.exportTrialAutocomplete.reset();
    this.exportModal.show();
  }

  setMonthAndYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>, control: string) {
    const ctrlValue = moment();
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    this.expiryDateFilterForm.get(control).setValue(ctrlValue.toISOString());
    this.loadCards(1);
    datepicker.close();
  }

  updateResult(event: MatDatepickerInputEvent<Moment>, control: string) {
    if (!event.value) {
      this.loadCards(1);
      return;
    }

    this.expiryDateFilterForm.get(control).setValue(event.value.toISOString());
    this.loadCards(1);
  }

  private toOffice(value: string | null): CaxtonCardOffice | null {
    return value !== null && value !== '' ? Number(value) as CaxtonCardOffice : null;
  }
}
